import { cn } from '@powerful/ui';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Icon, type IconName } from '../icons/icon';
import type { NavItem } from './nav-items';

/**
 * *Bottom tabs* — navegação primária no mobile, nos dois shells (PRD §3.5.1).
 *
 * O PRD justifica os tabs também no shell profissional *"para padronizar com o consumidor"*: a
 * *thumb zone* (§6.2) é a mesma independentemente do contexto, e alternar o padrão de navegação
 * entre os dois lados custaria reaprendizagem sem contrapartida.
 */

export interface BottomTabsProps {
  itens: readonly NavItem[];
  /** Nome acessível da região — os dois shells montam uma barra cada. */
  label: string;
  className?: string;
  /** Ícone por `id` do item (canvas T2.1 `tabbar`). Decorativo: o nome continua a ser o rótulo. */
  icones?: Readonly<Record<string, IconName>>;
}

export function BottomTabs({ itens, label, className, icones }: BottomTabsProps) {
  const { t } = useTranslation();

  return (
    <nav
      aria-label={label}
      className={cn(
        'sticky bottom-0 z-30 border-t border-surface-3 bg-surface-1',
        // Respeita a área segura do iOS: sem isto, o último separador fica sob a barra de gestos
        // do iPhone — visível, mas impossível de tocar sem acionar o gesto do sistema.
        'pb-[env(safe-area-inset-bottom)]',
        className,
      )}
    >
      <ul className="mx-auto flex max-w-content items-stretch justify-around">
        {itens.map((item) => (
          // `min-w-0`: sem ele, um item flex não encolhe abaixo da largura do próprio rótulo. A 320 px,
          // "Dashboard"/"Promotions" (ou "Tableau de bord") empurravam o último separador 14 px para
          // fora do ecrã — achado pela camada com sessão do `responsive.spec.ts` (T1.19).
          <li key={item.id} className="min-w-0 flex-1">
            <NavLink
              to={item.to}
              // `end` só no primeiro item: `/home` casaria com `/home/mapa` sem ele, e dois
              // separadores ficariam ativos ao mesmo tempo.
              end={item.to === itens[0]?.to}
              className={({ isActive }) =>
                cn(
                  'flex h-full min-h-[44px] w-full flex-col items-center justify-center gap-1',
                  // Rótulo longo quebra (com hífen no idioma do documento) em vez de alargar o item.
                  'px-1 py-2 text-center font-sans text-xs leading-tight break-words hyphens-auto',
                  'transition-colors duration-2 ease-soberano',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold',
                  // O ouro aqui é `--gold` (acento, 3:1), não `--metal`: metálico é reservado a
                  // CTA primário, Selo e logo. Ícone de navegação não é nenhum dos três.
                  isActive ? 'font-semibold text-gold' : 'text-ink-3 hover:text-ink-2',
                )
              }
            >
              {/*
                O `NavLink` do react-router aplica `aria-current="page"` no PRÓPRIO link quando a
                rota casa — e é aí que ele tem de estar, no elemento com papel de navegação, não
                num `<span>` interno que leitor nenhum associa ao destino.

                É o que comunica o separador ativo a quem NÃO vê a cor. Marcar o ativo apenas por
                dourado viola o RNF-ACC-05 ("sem dependência exclusiva de cor") — e é falha que o
                axe-core não reporta: a página tem contraste, tem nome acessível, e ainda assim
                deixa o utilizador sem saber onde está.
              */}
              {icones?.[item.id] ? <Icon name={icones[item.id]!} size={20} /> : null}
              {t(item.labelKey)}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
